import {
  Autocomplete,
  Box,
  Button,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import lscache from "lscache";
import React, { SyntheticEvent, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useVehicleFunctions } from "../../hooks/vehicles/useVehicleFunctions";
import IVehicle from "../../types/vehicle-type";
import { vehicleTypes } from "../../utils/vehicleTypes";
import CalendarSelect from "../Forms/CalendarSelect";
import TitleDescription from "../Forms/TitleDescription";
import GroupedCountries, {
  GroupedCurrencies,
} from "../Handlers/CountryHandler";
import PhotoHandler from "../Handlers/PhotoHandler";

interface VehicleFormProps {
  formData: IVehicle;
  setFormData: React.Dispatch<React.SetStateAction<IVehicle>>;
  handleSubmit: (e: React.FormEvent) => void;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  vehicleType: string;
  editing: boolean;
}

const VehicleForm: React.FC<VehicleFormProps> = ({
  formData,
  setFormData,
  handleSubmit,
  handleChange,
  vehicleType,
  editing,
}) => {
  const intl = useIntl();
  const { TranslatedVehicleOptions } = useVehicleFunctions();
  const translatedVehicleOptions = TranslatedVehicleOptions(vehicleTypes, vehicleType);

  useEffect(() => {
    if (formData.photos.length > 0) {
      lscache.set(vehicleType + "-photos", formData.photos, 30);
    }
  }, [vehicleType, formData.photos]);

  const handleTypeChange = (value: string) => {
    if (value === null) {
      return;
    }
    setFormData((prevFormData) => ({
      ...prevFormData,
      vehicleBody: value,
    }));
    lscache.set(vehicleType + "-vehicleBody", value, 30);
  };

  const handleCurrencyChange = (
    event: SyntheticEvent,
    newCurrency: string | null
  ) => {
    setFormData({ ...formData, currency: newCurrency || "EUR" });
    lscache.set(vehicleType + "-currency", newCurrency, 30);
  };

  const currencySymbol = Intl.NumberFormat("en-US", {
    style: "currency",
    currencyDisplay: "narrowSymbol",
    currency: formData.currency || "EUR",
  })
    .formatToParts(0)
    .filter((part) => part.type === "currency")
    .map((part) => part.value)
    .join("");

  const handleCountryChange = (
    event: SyntheticEvent,
    newValue: string | null,
    newCurrency: string | null
  ) => {
    setFormData((prevState: IVehicle) => ({
      ...prevState,
      country: newValue || "",
      currency: newCurrency || "",
    }));
    lscache.set(vehicleType + "-country", newValue, 30);
    lscache.set(vehicleType + "-currency", newCurrency, 30);
  };

  const handleDateChange = (date: dayjs.Dayjs, name: string) => {
    if (date) {
      setFormData({ ...formData, [name]: date });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TitleDescription
            title={formData.title}
            description={formData.description}
            handleChange={handleChange}
            fieldName="title"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            name="make"
            type="text"
            value={formData.make}
            onChange={handleChange}
            label={intl.formatMessage({
              id: "make",
              defaultMessage: "Make",
            })}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            name="model"
            type="text"
            value={formData.model}
            onChange={handleChange}
            label={intl.formatMessage({
              id: "model",
              defaultMessage: "Model",
            })}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            name="year"
            type="text"
            value={formData.year}
            onChange={handleChange}
            label={intl.formatMessage({
              id: "year",
              defaultMessage: "Year",
            })}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          {translatedVehicleOptions && (
            <Autocomplete
              disablePortal
              id="vehicles"
              options={translatedVehicleOptions}
              getOptionLabel={(option) =>
                option.translatedLabel || formData.vehicleBody
              }
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <Box
                    key={option.labelId}
                    id="vehicle-box"
                    component="li"
                    sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                    {...optionProps}
                  >
                    <FormattedMessage id={option.labelId} />
                  </Box>
                );
              }}
              value={
                translatedVehicleOptions.find(
                  (option) => option.labelId === formData.vehicleBody
                ) || translatedVehicleOptions[0]
              }
              onChange={(event, newValue) => {
                if (!newValue) {
                  return;
                }
                handleTypeChange(newValue.value);
              }}
              sx={{ width: 300 }}
              autoHighlight={true}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: "new-password",
                  }}
                  placeholder={intl.formatMessage({
                    id: "vehicle",
                    defaultMessage: "Select a vehicle",
                  })}
                  label={intl.formatMessage({
                    id: "vehicle",
                    defaultMessage: "Vehicles",
                  })}
                  name="vehicle"
                />
              )}
            />
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <CalendarSelect
            start={dayjs(formData.availableFrom)}
            handleDateChange={(date) => handleDateChange(date, "availableFrom")}
            name="availableFrom"
            id="available-from"
            message="Date available"
          />
        </Grid>
        <Grid item xs={12} md={12} lg={6}>
          <TextField
            name="address"
            type="text"
            value={formData.address}
            onChange={handleChange}
            label={intl.formatMessage({
              id: "address",
              defaultMessage: "Address",
            })}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <TextField
            name="city"
            type="text"
            value={formData.city}
            onChange={handleChange}
            label={intl.formatMessage({
              id: "city",
              defaultMessage: "City",
            })}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <GroupedCountries
            country={formData.country!}
            handleCountryChange={handleCountryChange}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            name="price"
            type="number"
            value={formData.price}
            onChange={handleChange}
            label={intl.formatMessage({
              id: "rent",
              defaultMessage: "Rent",
            })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {currencySymbol}
                </InputAdornment>
              ),
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <GroupedCurrencies
            currency={formData.currency}
            handleCurrencyChange={handleCurrencyChange}
          />
        </Grid>
        <Grid item xs={12}>
          <PhotoHandler
            initialPhotos={formData.photos || []}
            onPhotosChange={(newPhotos) =>
              setFormData({ ...formData, photos: newPhotos })
            }
            numberOfPhotos={formData.photos?.length || 0}
            source={formData.vehicleType}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4} mb={2}>
          <Button variant="contained" type="submit" fullWidth>
            {editing ? (
              <FormattedMessage id="update-vehicle" />
            ) : (
              <FormattedMessage id="submit-vehicle" />
            )}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default VehicleForm;
